import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import HomepageTab from '../components/admin/HomepageTab';
import AttributesTab from '../components/admin/AttributesTab';
import ImageSettingsTab from '../components/admin/ImageSettingsTab';
import AdPagesTab from '../components/admin/AdPagesTab';
import PagesTab from '../components/admin/PagesTab';
import BrandingTab from '../components/admin/BrandingTab';
import SeoTab from '../components/admin/SeoTab';
import AdsTab from '../components/admin/AdsTab';
import MenuTab from '../components/admin/MenuTab';
import FooterTab from '../components/admin/FooterTab';
import ContentTab from '../components/admin/ContentTab';
import ContactTab from '../components/admin/ContactTab';
import MessagesTab from '../components/admin/MessagesTab';
import LanguageTab from '../components/admin/LanguageTab';
import MembershipTypesTab from '../components/admin/MembershipTypesTab';
import Breadcrumb from '../components/Breadcrumb';
import {
  ArrowLeft,
  Shield,
  Loader2,
  Menu,
  Layout,
  FileText,
  MessageSquare,
  Globe,
  Search,
  Palette,
  Home,
  Monitor,
  Megaphone,
  Phone,
  Layers,
  Languages,
  Users,
  ChevronRight,
  ExternalLink,
  BookOpen,
  ImageIcon,
  Settings2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SiteSettingsPage = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('homepage');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(true);

  // Settings state
  const [ads, setAds] = useState([]);
  const [menu, setMenu] = useState([]);
  const [footerColumns, setFooterColumns] = useState([]);
  const [socialLinks, setSocialLinks] = useState([]);
  const [appLinks, setAppLinks] = useState({ android_url: '', ios_url: '' });
  const [content, setContent] = useState({});
  const [contactInfo, setContactInfo] = useState({});
  const [messages, setMessages] = useState([]);
  const [branding, setBranding] = useState({});
  const [seo, setSeo] = useState({});
  const [pages, setPages] = useState([]);
  const [systemPages, setSystemPages] = useState({});
  const [homepageSections, setHomepageSections] = useState([]);
  const [attributes, setAttributes] = useState({ categories: [], cities: [], genders: [], services: [], eye_colors: [], hair_colors: [], body_types: [], heights: [] });
  const [adPages, setAdPages] = useState({ home: true, search: true, detail: true, blog: true, contact: true, create_listing: false, edit_listing: false, dynamic_page: true });
  const [gridColumns, setGridColumns] = useState(6);
  const [mobileGridColumns, setMobileGridColumns] = useState(3);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [membershipTypes, setMembershipTypes] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    fetchAllSettings();
  }, [user, token, authLoading]);

  const fetchAllSettings = async () => {
    try {
      setLoading(true);
      const [adsRes, menuRes, footerRes, contentRes, contactRes, messagesRes, brandingRes, seoRes, pagesRes, systemPagesRes, homepageRes, attributesRes, adPagesRes, gridColsRes, membershipRes] = await Promise.all([
        axios.get(`${API}/settings/ads`, { headers }),
        axios.get(`${API}/settings/menu`, { headers }),
        axios.get(`${API}/settings/footer`, { headers }),
        axios.get(`${API}/settings/content`, { headers }),
        axios.get(`${API}/settings/contact-info`, { headers }),
        axios.get(`${API}/settings/contact/messages`, { headers }),
        axios.get(`${API}/settings/branding`, { headers }),
        axios.get(`${API}/settings/seo`, { headers }),
        axios.get(`${API}/pages/`, { headers }),
        axios.get(`${API}/settings/system-pages`, { headers }),
        axios.get(`${API}/settings/homepage-sections`, { headers }),
        axios.get(`${API}/settings/attributes/all`, { headers }),
        axios.get(`${API}/settings/ad-pages`, { headers }),
        axios.get(`${API}/settings/grid-columns`, { headers }),
        axios.get(`${API}/settings/membership-types`, { headers })
      ]);
      setAds(adsRes.data);
      setMenu(menuRes.data);
      setFooterColumns(footerRes.data.columns || []);
      setSocialLinks(footerRes.data.social_links || []);
      setAppLinks(footerRes.data.app_links || { android_url: '', ios_url: '' });
      setContent(contentRes.data);
      setContactInfo(contactRes.data);
      setMessages(messagesRes.data);
      setBranding(brandingRes.data);
      setSeo(seoRes.data);
      setPages(pagesRes.data);
      setSystemPages(systemPagesRes.data);
      setHomepageSections(homepageRes.data);
      setAttributes(attributesRes.data);
      setAdPages(adPagesRes.data);
      setGridColumns(Number(gridColsRes.data.grid_columns) || 6);
      setMobileGridColumns(Number(gridColsRes.data.mobile_grid_columns) || 3);
      setItemsPerPage(Number(gridColsRes.data.items_per_page) || 24);
      setMembershipTypes(membershipRes.data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Save functions
  const saveAds = async () => { setSaving(true); try { await axios.put(`${API}/settings/ads`, ads, { headers }); alert('Ads saved!'); } catch { alert('Error saving ads'); } finally { setSaving(false); } };
  const saveMenu = async () => { setSaving(true); try { await axios.put(`${API}/settings/menu`, menu, { headers }); alert('Menu saved!'); } catch { alert('Error saving menu'); } finally { setSaving(false); } };
  const saveFooter = async () => { setSaving(true); try { await Promise.all([axios.put(`${API}/settings/footer`, { columns: footerColumns, social_links: socialLinks }, { headers }), axios.put(`${API}/settings/app-links`, appLinks, { headers })]); alert('Footer saved!'); } catch { alert('Error saving footer'); } finally { setSaving(false); } };
  const saveContent = async () => { setSaving(true); try { await axios.put(`${API}/settings/content`, content, { headers }); alert('Content saved!'); } catch { alert('Error saving content'); } finally { setSaving(false); } };
  const saveContactInfo = async () => { setSaving(true); try { await axios.put(`${API}/settings/contact-info`, contactInfo, { headers }); alert('Contact info saved!'); } catch { alert('Error saving contact info'); } finally { setSaving(false); } };
  const saveBranding = async () => { setSaving(true); try { await axios.put(`${API}/settings/branding`, branding, { headers }); alert('Branding settings saved!'); } catch { alert('Error saving branding settings'); } finally { setSaving(false); } };
  const saveSeo = async () => { setSaving(true); try { await axios.put(`${API}/settings/seo`, seo, { headers }); alert('SEO settings saved!'); } catch { alert('Error saving SEO settings'); } finally { setSaving(false); } };
  const saveHomepageSections = async () => { setSaving(true); try { await axios.put(`${API}/settings/homepage-sections`, homepageSections, { headers }); alert('Homepage sections saved!'); } catch { alert('Error saving homepage sections'); } finally { setSaving(false); } };
  const saveAdPages = async () => { setSaving(true); try { await axios.put(`${API}/settings/ad-pages`, adPages, { headers }); alert('Ad settings saved!'); } catch { alert('Error saving ad settings'); } finally { setSaving(false); } };
  const saveGridColumns = async () => { setSaving(true); try { await axios.put(`${API}/settings/grid-columns`, { grid_columns: gridColumns, mobile_grid_columns: mobileGridColumns, items_per_page: itemsPerPage }, { headers }); alert('Grid settings saved!'); } catch { alert('Error saving grid settings'); } finally { setSaving(false); } };
  const saveMembershipTypes = async () => { setSaving(true); try { await axios.put(`${API}/settings/membership-types`, { membership_types: membershipTypes }, { headers }); alert('Membership types saved!'); } catch { alert('Failed to save membership types'); } finally { setSaving(false); } };

  const savePage = async (pageData, editingPage) => {
    setSaving(true);
    try {
      if (editingPage?.id) { await axios.put(`${API}/pages/${editingPage.id}/`, pageData, { headers }); }
      else { await axios.post(`${API}/pages/`, pageData, { headers }); }
      fetchAllSettings();
      alert('Page saved!');
    } catch (error) { alert(error.response?.data?.detail || 'Error saving page'); }
    finally { setSaving(false); }
  };

  const deletePage = async (pageId) => { try { await axios.delete(`${API}/pages/${pageId}`, { headers }); setPages(pages.filter(p => p.id !== pageId)); } catch (error) { console.error('Error:', error); } };
  const saveSystemPage = async (pageKey, pageData) => { setSaving(true); try { await axios.put(`${API}/settings/system-pages/${pageKey}`, pageData, { headers }); setSystemPages({ ...systemPages, [pageKey]: pageData }); alert('Page settings saved!'); } catch { alert('Error saving page settings'); } finally { setSaving(false); } };
  const deleteMessage = async (messageId) => { try { await axios.delete(`${API}/settings/contact/messages/${messageId}`, { headers }); setMessages(messages.filter(m => m.id !== messageId)); } catch (error) { console.error('Error:', error); } };
  const markAsRead = async (messageId) => { try { await axios.put(`${API}/settings/contact/messages/${messageId}/read`, {}, { headers }); setMessages(messages.map(m => m.id === messageId ? { ...m, read: true } : m)); } catch (error) { console.error('Error:', error); } };

  if (!user || user.role !== 'admin') return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <p className="text-zinc-500 text-sm">Loading settings...</p>
        </div>
      </div>
    );
  }

  const unreadCount = messages.filter(m => !m.read).length;

  const sidebarSections = [
    {
      title: 'Content Management',
      items: [
        { id: 'homepage', icon: Home, label: 'Homepage' },
        { id: 'pages', icon: Globe, label: 'Pages' },
        { id: 'content', icon: FileText, label: 'Site Content' },
      ]
    },
    {
      title: 'Appearance',
      items: [
        { id: 'branding', icon: Palette, label: 'Branding & Logo' },
        { id: 'menu', icon: Menu, label: 'Menu Management' },
        { id: 'footer', icon: Layout, label: 'Footer' },
      ]
    },
    {
      title: 'SEO & Ads',
      items: [
        { id: 'seo', icon: Search, label: 'SEO Settings' },
        { id: 'advseo', icon: Layers, label: 'Advanced SEO', link: '/admin/seo', badge: 'Pro' },
        { id: 'ads', icon: Megaphone, label: 'Ad Banners' },
        { id: 'adpages', icon: Monitor, label: 'Ad Pages' },
      ]
    },
    {
      title: 'System Settings',
      items: [
        { id: 'attributes', icon: Layers, label: 'Attributes' },
        { id: 'images', icon: ImageIcon, label: 'Images & CDN' },
        { id: 'language', icon: Languages, label: 'Language Settings' },
        { id: 'membership-types', icon: Users, label: 'Membership Types' },
      ]
    },
    {
      title: 'Contact',
      items: [
        { id: 'contact', icon: Phone, label: 'Contact Info' },
        { id: 'messages', icon: MessageSquare, label: 'Messages', badge: unreadCount > 0 ? unreadCount : null },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[
        { label: 'Admin Panel', href: '/admin' },
        { label: 'Site Settings', href: null }
      ]} />

      {/* Top Header */}
      <header className="bg-zinc-900/80 backdrop-blur-sm border-b border-zinc-800/50 sticky top-0 z-30">
        <div className="flex items-center justify-between px-4 md:px-6 h-14">
          <div className="flex items-center gap-3 md:gap-4">
            <button onClick={() => navigate('/admin')} className="text-zinc-500 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden text-zinc-400 hover:text-white transition-colors">
              <Settings2 className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-emerald-500/10 rounded-lg flex items-center justify-center">
                <Shield className="w-4 h-4 text-emerald-400" />
              </div>
              <div>
                <h1 className="text-sm font-semibold text-white leading-none">Site Settings</h1>
                <p className="text-[11px] text-zinc-500 mt-0.5">General configuration</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white h-8 text-xs"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
              View Site
            </Button>
            <Button
              onClick={() => navigate('/admin/blog')}
              size="sm"
              className="bg-purple-600 hover:bg-purple-700 h-8 text-xs"
            >
              <BookOpen className="w-3.5 h-3.5 mr-1.5" />
              Blog Management
            </Button>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-full md:w-60 min-h-[calc(100vh-3.5rem)] bg-zinc-900/40 border-r border-zinc-800/50 p-4 md:sticky md:top-14 md:self-start overflow-y-auto max-h-[calc(100vh-3.5rem)]`}>
          {sidebarSections.map((section, sIdx) => (
            <div key={sIdx} className={sIdx > 0 ? 'mt-5' : ''}>
              <p className="text-[10px] font-semibold text-zinc-600 uppercase tracking-widest px-3 mb-2">
                {section.title}
              </p>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.link) navigate(item.link);
                        else setActiveTab(item.id);
                        setMobileMenuOpen(false);
                      }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all text-sm ${
                        isActive
                          ? 'bg-emerald-500/10 text-emerald-400'
                          : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-400' : ''}`} />
                      <span className="flex-1 truncate">{item.label}</span>
                      {item.badge === 'Pro' && (
                        <Badge className="bg-emerald-500/10 text-emerald-400 text-[10px] px-1.5 py-0">Pro</Badge>
                      )}
                      {typeof item.badge === 'number' && (
                        <span className="bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{item.badge}</span>
                      )}
                      {item.link && <ChevronRight className="w-3.5 h-3.5 text-zinc-600" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </aside>

        {/* Main Content */}
        <main className={`flex-1 p-4 md:p-6 min-w-0 ${mobileMenuOpen ? 'hidden md:block' : 'block'}`}>
          {activeTab === 'homepage' && (
            <HomepageTab homepageSections={homepageSections} setHomepageSections={setHomepageSections} saving={saving} onSave={saveHomepageSections} token={token} gridColumns={gridColumns} setGridColumns={setGridColumns} mobileGridColumns={mobileGridColumns} setMobileGridColumns={setMobileGridColumns} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} onSaveGridColumns={saveGridColumns} />
          )}
          {activeTab === 'pages' && (
            <PagesTab pages={pages} systemPages={systemPages} onDeletePage={deletePage} onSavePage={savePage} onSaveSystemPage={saveSystemPage} saving={saving} />
          )}
          {activeTab === 'branding' && (
            <BrandingTab branding={branding} setBranding={setBranding} saving={saving} onSave={saveBranding} token={token} />
          )}
          {activeTab === 'seo' && (
            <SeoTab seo={seo} setSeo={setSeo} saving={saving} onSave={saveSeo} />
          )}
          {activeTab === 'ads' && (
            <AdsTab ads={ads} setAds={setAds} saving={saving} onSave={saveAds} token={token} />
          )}
          {activeTab === 'menu' && (
            <MenuTab menu={menu} setMenu={setMenu} saving={saving} onSave={saveMenu} />
          )}
          {activeTab === 'footer' && (
            <FooterTab footerColumns={footerColumns} setFooterColumns={setFooterColumns} socialLinks={socialLinks} setSocialLinks={setSocialLinks} appLinks={appLinks} setAppLinks={setAppLinks} saving={saving} onSave={saveFooter} />
          )}
          {activeTab === 'content' && (
            <ContentTab content={content} setContent={setContent} saving={saving} onSave={saveContent} />
          )}
          {activeTab === 'contact' && (
            <ContactTab contactInfo={contactInfo} setContactInfo={setContactInfo} saving={saving} onSave={saveContactInfo} />
          )}
          {activeTab === 'messages' && (
            <MessagesTab messages={messages} onMarkAsRead={markAsRead} onDeleteMessage={deleteMessage} />
          )}
          {activeTab === 'attributes' && (
            <AttributesTab attributes={attributes} setAttributes={setAttributes} token={token} />
          )}
          {activeTab === 'adpages' && (
            <AdPagesTab adPages={adPages} setAdPages={setAdPages} saving={saving} onSave={saveAdPages} />
          )}
          {activeTab === 'images' && (
            <ImageSettingsTab token={token} />
          )}
          {activeTab === 'language' && (
            <LanguageTab />
          )}
          {activeTab === 'membership-types' && (
            <MembershipTypesTab membershipTypes={membershipTypes} setMembershipTypes={setMembershipTypes} saving={saving} onSave={saveMembershipTypes} />
          )}
        </main>
      </div>
    </div>
  );
};

export default SiteSettingsPage;
